import { SetURLSearchParams } from "react-router-dom";

/**
 * Every param write still waiting to show up in the browser URL, keyed by param
 * name. `null` means "delete this param".
 *
 * react-router's setSearchParams replaces the whole query string, and its
 * updater is handed the params of the location it can currently see. Two hooks
 * writing in the same commit — the common case, since several of them sync on
 * mount — therefore both compute from the pre-commit params and the second one
 * drops what the first just wrote. Nothing brings it back: the loser's effect
 * does not re-run, because from its point of view neither its own value nor the
 * param it wanted changed.
 *
 * Buffering the intents makes every writer re-apply the others, so the last
 * write of a commit carries all of them. The pathname is recorded so intents
 * cannot follow the user to another page.
 */
type PendingURLParam = { value: string | null; pathname: string };
const pendingURLParams = new Map<string, PendingURLParam>();

/**
 * react-router's setSearchParams navigates to a bare "?search" target, which
 * resolves with no hash — wiping any existing window.location.hash (e.g. a
 * hash-based tab selector) as an unrelated side effect of an unrelated param
 * write. Call this right after such a setSearchParams call, with the hash
 * captured beforehand, to restore it.
 *
 * Restoring the address bar alone is not enough: react-router's own location
 * (what useLocation() returns) only re-syncs from window.location on a
 * "popstate" event, and a plain replaceState is invisible to it. Without the
 * dispatch below, the router keeps reporting the hash-stripped location
 * setSearchParams just produced — e.g. a hash-based tab silently reverting to
 * its default — even though the address bar shows the restored hash.
 */
export function restoreHashAfterSearchParamsWrite(hash: string): void {
  if (!hash) return;
  window.history.replaceState(
    window.history.state,
    "",
    window.location.pathname + window.location.search + hash
  );
  window.dispatchEvent(
    new PopStateEvent("popstate", { state: window.history.state })
  );
}

/**
 * Write (or, with `null`, delete) one search param without discarding the
 * writes other hooks made in the same commit.
 */
export function writeURLParam(
  key: string,
  value: string | null,
  setSearchParams: SetURLSearchParams
): void {
  const pathname = window.location.pathname;
  const hash = window.location.hash;
  pendingURLParams.set(key, { value, pathname });
  setSearchParams(
    (prev) => {
      const next = new URLSearchParams(prev);
      pendingURLParams.forEach((pending, pendingKey) => {
        if (pending.pathname !== pathname) return;
        if (pending.value === null) {
          next.delete(pendingKey);
        } else {
          next.set(pendingKey, pending.value);
        }
      });
      return next;
    },
    { replace: true }
  );
  restoreHashAfterSearchParamsWrite(hash);
}

/**
 * Drop the intents the URL now reflects, and anything left over from another
 * page. Without this a param that is later removed — by a plain link, or by the
 * user editing the URL — would be resurrected by the next unrelated write, and
 * the buffer would outlive the page (and, in tests, the test).
 *
 * Call it from an effect keyed on the current search params. Dropping a settled
 * intent cannot lose anything, because a settled param is by definition already
 * in what the next writer is handed.
 */
export function forgetSettledURLParams(search: URLSearchParams): void {
  const pathname = window.location.pathname;
  pendingURLParams.forEach((pending, key) => {
    const isSettled = (search.get(key) ?? null) === pending.value;
    if (pending.pathname !== pathname || isSettled) {
      pendingURLParams.delete(key);
    }
  });
}

/**
 * Returns true for Moment.js objects and native Dates — values that should be
 * treated as primitives (leaf nodes) rather than plain objects to be iterated.
 * Moment is detected via its internal _isAMomentObject flag.
 */
function isMomentOrDate(value: unknown): boolean {
  if (value === null || typeof value !== "object") return false;
  return (
    value instanceof Date ||
    (value as Record<string, unknown>)["_isAMomentObject"] === true
  );
}

/**
 * Parse a JSON string safely, returning undefined on failure.
 */
export function parseJSON<T>(raw: string): T | undefined {
  try {
    return JSON.parse(raw) as T;
  } catch {
    return undefined;
  }
}

/**
 * Recursively merge `override` into `defaults`.
 * - Plain objects: recursively merge key by key (missing keys use defaults)
 * - Arrays and primitives: always take the override value as-is
 *
 * Used to fill in missing keys when a URL-stored value was written with
 * stripEmpty (which removes empty arrays/strings), so the loaded state is
 * always structurally complete even if the URL is compact.
 */
export function deepMerge<T>(defaults: T, override: T): T {
  if (
    typeof defaults !== "object" ||
    defaults === null ||
    Array.isArray(defaults)
  ) {
    return override ?? defaults;
  }
  const result = { ...(defaults as Record<string, unknown>) };
  const overrideObj = override as Record<string, unknown>;
  for (const key of Object.keys(overrideObj)) {
    const defVal = result[key];
    const overVal = overrideObj[key];
    if (
      typeof defVal === "object" &&
      defVal !== null &&
      !Array.isArray(defVal) &&
      !isMomentOrDate(defVal) &&
      typeof overVal === "object" &&
      overVal !== null &&
      !Array.isArray(overVal) &&
      !isMomentOrDate(overVal)
    ) {
      result[key] = deepMerge(defVal, overVal);
    } else {
      result[key] = overVal;
    }
  }
  return result as T;
}

/**
 * Recursively remove the "realValue" key from any nested object.
 *
 * realValue is a computed field in SelectorValueWithIncludeExclude that holds
 * all non-selected options in exclude mode — potentially hundreds of entries.
 * It is never needed for URL storage since matchesFilter only uses
 * mode + displayValue, so stripping it keeps URLs short.
 */
export function stripRealValues<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map(stripRealValues) as unknown as T;
  }
  if (isMomentOrDate(value)) return value;
  if (value !== null && value !== undefined && typeof value === "object") {
    const obj = value as Record<string, unknown>;
    const result: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(obj)) {
      if (k === "realValue") continue;
      result[k] = stripRealValues(v);
    }
    return result as unknown as T;
  }
  return value;
}

/**
 * Recursively remove "empty" values (empty arrays, empty strings, null,
 * undefined, empty objects) so that a filter like `{"alertTypes":[]}` is
 * treated the same as `{}` when deciding whether the URL param should be kept.
 */
export function stripEmpty<T>(value: T): T {
  if (Array.isArray(value)) {
    return value
      .filter((item) => item !== null && item !== undefined && item !== "")
      .map(stripEmpty) as unknown as T;
  }
  // Treat Moment/Date as a primitive leaf — do not iterate their internal properties.
  if (isMomentOrDate(value)) {
    return value;
  }
  if (value !== null && value !== undefined && typeof value === "object") {
    const result: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      const stripped = stripEmpty(v);
      const empty =
        stripped === null ||
        stripped === undefined ||
        stripped === "" ||
        (Array.isArray(stripped) && stripped.length === 0) ||
        (typeof stripped === "object" &&
          !Array.isArray(stripped) &&
          Object.keys(stripped as object).length === 0);
      if (!empty) {
        result[k] = stripped;
      }
    }
    return result as unknown as T;
  }
  return value;
}
